
/**
 * The navigation entries as the OPERATOR sees them — one label and one glyph
 * per section, for every surface that draws navigation.
 *
 * This lived in `ClinicLayout.jsx` while the sidebar was the only thing that
 * drew a nav. The phone's bottom bar draws the same sections, and it cannot
 * import the layout that renders it without a cycle — so the list moved here,
 * to the layer both surfaces already depend on.
 *
 * POLICY still lives in `nav.js`: which sections a person may see, which
 * permission each needs, and where a bare `/c/:ref/` lands. Splitting copy
 * from policy is why a permission change never touches a translation, and why
 * renaming a section never touches an access rule.
 *
 * The glyphs are hand-drawn rather than lucide because they are the product's
 * own section marks and read at 20px in a gradient rail; the kit owns generic
 * icons, an app owns its own landmarks.
 */

export const NAV_ITEMS = (t) => ([
  {
    id: 'reception',
    label: t('nav.items.reception'),
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" /></svg>,
  },
  {
    id: 'patients',
    label: t('nav.items.patients'),
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" /></svg>,
  },
  {
    /* «کالاها و خدمات» — what the clinic stocks, what it offers and what it
       owns: انبار, خدمات و تعرفه, تجهیزات, تأمین‌کنندگان, بیمه. It was split
       from «مدیریت هزینه‌ها» (the two are the halves of one former row), and
       sits after «بیماران» in the order the clinic set on 2026-09-30.

       The glyph is a box with a price tag: the ledger above it keeps the lined
       sheet, so the pair reads as "what it cost" and "what it is". Hand-drawn
       like its siblings — the kit owns generic icons, an app owns its own
       section marks. */
    id: 'goods',
    label: t('nav.items.goods'),
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M12 2 3 6.5v11L12 22l9-4.5v-11L12 2zm0 2.2 6.5 3.3L12 10.8 5.5 7.5 12 4.2zM5 9.2l6 3v7.3l-6-3V9.2zm8 10.3v-7.3l6-3v7.3l-6 3z" /><path d="M16.5 12.6a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2z" opacity=".35" /></svg>,
  },
  {
    /* «تقویم و نوبت‌دهی» is not a row here any more, and is no longer a screen
       at all. It became a view of پذیرش on 2026-09-06 and was removed from the
       panel on 2026-09-13: پذیرش shows the desk's own work, and a week grid of a
       practitioner's presence is not it — the شیفت‌ها that define it are edited
       in that person's drawer on «همکاران», and booking is «ثبت نوبت» here. */
    /* «حقوق و دستمزد» is not a row here any more either. It became a TAB of
       «همکاران» on 2026-09-13: it is something you do TO the people on this
       page — every line computed from a contract written in their drawer — and
       a second row for the same population made the round trip between them
       look like two jobs. Its permission did not move: the tab is absent, not
       disabled, without `payroll.view`, and the money an accountant reads is a
       view of «حسابداری». */
    id: 'people',
    label: t('nav.items.people'),
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" /></svg>,
  },
  {
    /* «مدیریت هزینه‌ها» — the expense ledger, قبوض, and the definitions that
       classify them. It was «عملیات و هزینه», the merged home of هزینه‌ها +
       انبار و کالا + خدمات, until 2026-09-12: sixteen rail destinations
       answering two different questions is not one screen, it is two wearing one
       label, so the warehouse and the service list moved next door
       (docs/CLINIC_EXPENSES_AND_GOODS_SPLIT_DESIGN.md). The route kept its path
       — the bank's return URL and the reports' attention links name it. */
    id: 'operations',
    label: t('nav.items.operations'),
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" /><path d="M19.5 3.5 21 5l-4 4-2-2 1.5-1.5L17 6.5l2.5-3z" opacity=".35" /></svg>,
  },
  {
    /* «حسابداری» — the Financial Operations hub, in the slot «پرداخت‌ها» held
       until 2026-09-12. The receipts ledger and the claims ledger moved INSIDE
       it rather than sitting beside it: the old row said «پرداخت‌ها» and meant
       money IN, while an accountant means money OUT by the same word, and two
       rows using one word for opposite directions is the duplication the hub
       exists to end (docs/CLINIC_ACCOUNTING_FINOPS_DESIGN.md D3).
       A ledger: a bound book with ruled lines and a column of figures. */
    id: 'accounting',
    label: t('nav.items.accounting'),
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M5 3h13a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm0 2v14h13v-2h-3v-2h3v-2h-3v-2h3V9h-3V7h3V5H5zm2 2h5v2H7V7zm0 4h5v2H7v-2zm0 4h5v2H7v-2z" /></svg>,
  },
  {
    id: 'reports',
    label: t('nav.items.reports'),
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" /></svg>,
  },
  {
    /* «فروشگاه» — what the clinic SELLS: the storefront, its listings, the
       orders people placed, and the clinic's seat in the Parsina Marketplace
       (docs/CLINIC_STORE_MARKETPLACE_DESIGN.md §8.1). The commercial
       continuation of «کالاها و خدمات»: every listing is created FROM an item or a service declared
       there — the pair reads as "what we have" and "what we sell".

       The glyph is a storefront: a scalloped awning over a shop with a door,
       the awning's inner scallops picked out at the sibling's accent opacity.
       Hand-drawn like the rest — the kit owns generic icons, an app owns its
       own section marks. */
    id: 'store',
    label: t('nav.items.store'),
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M4 3h16l2 5.2c0 1.5-1 2.8-2.5 2.8-.9 0-1.7-.5-2.1-1.2-.5.7-1.3 1.2-2.2 1.2-.9 0-1.7-.5-2.2-1.2-.5.7-1.3 1.2-2.2 1.2-.9 0-1.7-.5-2.1-1.2C8.3 10.5 7.5 11 6.6 11 5.1 11 4 9.7 4 8.2L4 3zM5 12.6c.5.2 1 .4 1.6.4 1 0 1.8-.3 2.6-.9.7.6 1.6.9 2.6.9s1.9-.3 2.6-.9c.7.6 1.6.9 2.6.9.6 0 1.1-.1 1.6-.4V21H5v-8.4zM9 15v4h3v-4H9z" /><path d="M6.2 5h11.6l1 2.6H5.2L6.2 5z" opacity=".35" /></svg>,
  },
  {
    // «اشتراک کلینیک» + «معرفی کلینیک», merged. They described one subject — the
    // clinic as an entity — at one frequency, while every row above is a job
    // somebody does all day, so two rows for them diluted the operational nav.
    id: 'clinic',
    label: t('nav.items.clinic'),
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M12 3 2 8v13h7v-6h6v6h7V8L12 3zm1 6h2v2h2v2h-2v2h-2v-2h-2v-2h2V9z" /></svg>,
  },
  {
    // Person-scoped, not clinic-scoped: an invitation belongs to the person, and
    // this row appears only while one is waiting. A permanent «دعوت‌ها (۰)» would
    // be the standing nag that trains people to stop reading the sidebar — which
    // then fails when something real needs attention.
    id: 'invitations',
    label: t('nav.items.invitations'),
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" /></svg>,
  },
]);
